/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useState } from 'react'
import {
    Space,
    Badge,
    message,
    Popover,
    Button,
    Menu,
    Dropdown,
    Tooltip,
    Avatar,
} from 'antd'
import {
    DownloadOutlined,
    InfoCircleFilled,
    LoadingOutlined,
    MoreOutlined,
} from '@ant-design/icons'
import i18next from 'i18next'
import moment from 'moment'
import { PDFDownloadLink } from '@react-pdf/renderer'

import { AppContext } from '../context/AppContext'

import {
    formatNb,
    formatDate,
    displayOrderDetails,
    downloadCSV,
} from '../utils/utils'

import { getOrders, EditOrders, getCommunities, SERVER } from '../network/API'

import PageWrap from '../components/layout/PageWrap'
import TableBlock from '../components/tables/TableBlock'
import { OrderStatus, PaymentMethod } from '../components/tables/cells/Status'
import DynamicStrings from '../components/DynamicStrings'
import MyPDFDocument from 'src/components/MyPDF'

import QRCode from 'qrcode'
import petqrbg from '../images/petqrbg.png'
import mergeImages from 'merge-images'
import { loadImageSize, scaleQrOverlay } from '../utils/qrOverlay'
import { useTranslation } from 'react-i18next'

const DIRECT_SALES_EXPORT_MAX_ROWS = (() => {
    const parsedValue = Number(process.env.REACT_APP_DIRECT_SALES_EXPORT_MAX_ROWS)
    if (Number.isInteger(parsedValue) && parsedValue > 0) {
        return parsedValue
    }
    return 2000
})()

const DIRECT_SALES_EXPORT_HEADERS = [
    'Order ID',
    'Customer Name',
    'Phone Number',
    'Order Date',
    'Order Time',
    'Order Details',
    'Order Details (Product Name)',
    'Comment',
    'Delivery Tracking Number',
    'Delivery Date',
    'Delivery Fee',
    'Voucher',
    'Total',
    'Payment Status',
    'Tracking Status',
]

const displayOrderShortDetails = (order) =>
    [
        ...(order?.packs
            ?.filter((pack) => pack.amount)
            ?.map((pack) => `${pack.amount} ${pack.shortName}`) || []),
        ...(order?.singleItems
            ?.filter((item) => item.amount)
            ?.map(
                (item) =>
                    `${item.amount} ${
                        item.shortName ||
                        item?.product?.name[i18next.language]
                    }`
            ) || []),
    ].join(', ')

const mapOrderToExportRow = (order) => [
    order?.orderID || '',
    order?.customer?.id
        ? order?.customer?.name || ''
        : order?.customerDaily?.customerName || '',
    order?.customerAddress?.phone || '',
    order?.orderDate ? formatDate(order.orderDate) : '',
    order?.orderDate ? moment(order.orderDate).format('HH:mm') : '',
    displayOrderShortDetails(order),
    displayOrderDetails(order),
    order?.comment || '',
    order?.deliveryTrackingID || '',
    order?.deliveryDate ? formatDate(order.deliveryDate) : '',
    order?.deliveryFee ?? '',
    order?.vouchers?.map((voucher) => voucher?.amount).join(', ') || '0',
    order?.actualAmount ?? '',
    order?.paymentStatus || '',
    order?.trackingStatus || '',
]

const DirectSalesOrder = ({ history }) => {
    const { user } = useContext(AppContext)
    const { t } = useTranslation()

    const commuFilter =
        user.role === 'admin' || user.role === 'super-admin'
            ? {}
            : { community: { $in: user.communities } }

    return (
        <PageWrap title={t('DIRECT_SALES_ALL_CUSTOMER_ORDERS')}>
            <TableBlock
                columns={columns}
                filters={filters}
                name={'getOrders'}
                request={getOrders}
                pageId={commuFilter}
                otherFilters={{
                    // status: { $ne: 'discontinued' },
                    type: { $in: ['direct_sale', 'daily'] },
                    valid: true,
                    ...commuFilter,
                }}
                pageSize={80}
            />
        </PageWrap>
    )
}

const ExportDirectSalesOrdersBtn = ({ requestKeys, commuFilter }) => {
    const [isExporting, setIsExporting] = useState(false)
    const { t } = useTranslation()

    const exportOrders = async () => {
        if (!requestKeys?.[1]) {
            message.error(t('ERROR'))
            return
        }

        const baseRequestPayload = requestKeys[1]
        const exportRange = [0, DIRECT_SALES_EXPORT_MAX_ROWS - 1]

        setIsExporting(true)
        try {
            const exportResult = await getOrders({
                queryKey: [
                    'getOrdersCsvExport',
                    {
                        ...baseRequestPayload,
                        filter: {
                            type: { $in: ['direct_sale', 'daily'] },
                            valid: true,
                            ...commuFilter,
                            ...(baseRequestPayload.filter || {}),
                        },
                        range: exportRange,
                    },
                ],
            })

            const rows = (exportResult?.list || []).map(mapOrderToExportRow)

            downloadCSV(
                DIRECT_SALES_EXPORT_HEADERS,
                rows,
                `direct-sales-orders-${moment().format('YYYY-MM-DD')}`
            )

            if (exportResult?.range > DIRECT_SALES_EXPORT_MAX_ROWS) {
                message.warning(
                    `Only the first ${DIRECT_SALES_EXPORT_MAX_ROWS} rows will be exported because the filtered results exceed the export limit.`,
                    8
                )
            } else {
                message.success(`CSV exported (${rows.length} rows).`)
            }
        } catch (error) {
            message.error('Failed to export CSV.')
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <Space className='relative'>
            <Button
                type="primary"
                loading={isExporting}
                icon={isExporting ? <LoadingOutlined /> : <DownloadOutlined />}
                onClick={exportOrders}
            >
                {t('ANALYTICS_EXPORT_CSV')}
            </Button>
            <Tooltip
                className='export-tooltip'
                title={`Export is limited to ${DIRECT_SALES_EXPORT_MAX_ROWS} rows.`}
            >
                <InfoCircleFilled style={{ color: 'grey' }} />
            </Tooltip>
        </Space>
    )
}

const filters = (history, selectedKeys, isLoading, commuFilter) => ({
    fields: [
        {
            type: 'select',
            label: 'ROLES_COMMUNITY',
            key: 'community',
            options: getCommunities,
        },
        {
            type: 'select',
            label: 'DIRECT_SALES_ORDER_KIND',
            default: '',
            key: 'type',
            options: [
                { key: 'daily', label: 'DIRECT_SALES_ORDER_KIND_DAILY' },
                {
                    key: 'direct_sale',
                    label: 'DIRECT_SALES_ORDER_KIND_NOT_DAILY',
                },
            ],
        },
        {
            type: 'spe',
            label: 'ROLES_NAME',
            key: 'customer.name',
            noRegex: true,
        },
        {
            type: 'text',
            label: 'OFFER_LIVE_ORDER_ID',
            key: 'orderID',
            onCell: () => {
                return {
                    onClick: (e) => e.stopPropagation(),
                }
            },
        },
        // {
        //     type: 'text',
        //     label: 'SERVICES_ORDER_LIST_TRANSACTION_NUMBER',
        //     key: 'transactionID',
        // },
        {
            type: 'text',
            label: 'DIRECT_SALES_DELIVERY_TRACKING_NUMBER',
            key: 'deliveryTrackingID',
        },
        {
            type: 'daterange',
            label: 'ANALYTICS_DATES',
            key: ['dateStart', 'dateEnd'],
        },
    ],
    actions: [
        {
            label: 'ANALYTICS_EXPORT_CSV',
            isActive: !isLoading,
            component: (elemId, requestKeys) => (
                <ExportDirectSalesOrdersBtn
                    requestKeys={requestKeys}
                    commuFilter={commuFilter}
                />
            ),
        },
        {
            label: 'DIRECT_SALES_CREATE_DAILY_ORDER',
            isActive: true,
            action: () => history.push('/daily-orders/create'),
        },
    ],
    tabs: {
        key: 'paymentStatus',
        default: '',
        options: [
            { key: '', label: 'OFFERS_ALL' },
            { key: 'pending', label: 'pending' },
            { key: 'paid', label: 'paid' },
            { key: 'ccl', label: 'ccl' },
            { key: 'refunding', label: 'refund' },
            { key: 'rfd', label: 'refunded' },
        ],
    },
})

const columns = (goTo, keyRequest) => [
    {
        title: <DynamicStrings id="OFFER_LIVE_ORDER_ID" />,
        dataIndex: 'orderID',
        key: 'orderID',
    },
    {
        title: <DynamicStrings id="ROLES_NAME" />,
        key: 'customer',
        render: (order) =>
            order?.customer?.id
                ? order.customer.name
                : order.customerDaily?.customerName,
        sorter: true,
    },
    {
        title: <DynamicStrings id="DIRECT_SALES_PHONE_NUMBER" />,
        // dataIndex: 'customer',
        render: (e) => e.customerAddress?.phone,
        key: 'customer',
        sorter: true,
    },
    {
        title: <DynamicStrings id="SERVICES_ORDER_LIST_DATE" />,
        dataIndex: 'orderDate',
        render: (date) => formatDate(date),
        key: 'orderDate',
        sorter: true,
    },
    {
        title: <DynamicStrings id="SERVICES_ORDER_TIME" />,
        dataIndex: 'orderDate',
        render: (date) => moment(date).format('HH:mm'),
        key: 'orderDate',
        // sorter: true,
    },
    {
        title: <DynamicStrings id="SERVICES_ORDER_LIST_ORDER_DETAILS" />,
        // dataIndex: 'packs',
        render: (e) => {
            const toDisplay = [
                ...e.packs
                    ?.filter((e) => e.amount)
                    ?.map((pack, i) => `${pack.amount} ${pack.shortName}`),
                ...e.singleItems
                    ?.filter((e) => e.amount)
                    ?.map(
                        (item) =>
                            `${item.amount} ${
                                item.shortName ||
                                item?.product?.name[i18next.language]
                            }`
                    ),
            ].join(', ')

            return <Tooltip title={displayOrderDetails(e)}>{toDisplay}</Tooltip>
        },

        key: 'packs',
    },
    // {
    //     title: <DynamicStrings id="SERVICES_ORDER_LIST_TRANSACTION_NUMBER" />,
    //     dataIndex: 'transactionID',
    //     key: 'transactionID',
    // },
    {
        title: <DynamicStrings id="OFFER_LIVE_COMMENT" />,
        dataIndex: 'comment',
        key: 'comment',
    },
    {
        title: <DynamicStrings id="DIRECT_SALES_DELIVERY_TRACKING_NUMBER" />,
        dataIndex: 'deliveryTrackingID',
        key: 'deliveryTrackingID',
    },
    {
        title: <DynamicStrings id="OFFER_LIVE_DELIVERY_DATE" />,
        dataIndex: 'deliveryDate',
        // render: (deliveryDate) =>
        //     deliveryDate && moment(deliveryDate).isValid()
        //         ? formatDate(deliveryDate)
        //         : deliveryDate,
        render: (deliveryDate) => {
            const dateFormat = 'YYYY-MM-DDTHH:mm:SS.sssZ'

            if (
                deliveryDate &&
                moment(deliveryDate, dateFormat, true).isValid()
            ) {
                return formatDate(deliveryDate)
            } else {
                return deliveryDate
            }
        },
        key: 'deliveryDate',
        sorter: true,
    },
    {
        title: <DynamicStrings id="OFFER_LIVE_DELIVERY_FEE" suffix={'(¥)'} />,
        dataIndex: 'deliveryFee',
        key: 'deliveryFee',
    },
    {
        title: <DynamicStrings id="OFFER_LIVE_VOUCHER" />,
        dataIndex: 'vouchers',
        render: (vouchers) =>
            vouchers.length
                ? vouchers?.map(
                      (vouchers, i) => (i === 0 ? '' : ', ') + vouchers.amount
                  )
                : '0',
        key: 'vouchers',
    },
    {
        title: <DynamicStrings id="OFFER_LIVE_TOTAL" suffix="(¥)" />,
        render: ({ actualAmount, id }) => (
            <PaymentMethod
                actualAmount={formatNb(actualAmount)}
                id={id}
                keyRequest={keyRequest}
            />
        ),
        onCell: (record, rowIndex) => {
            if (record.paymentMethod === 'alipay') {
                return {
                    className: 'is-alipay',
                }
            }
        },
        key: 'actualAmount',
        sorter: true,
    },
    {
        // title: 'Paid',
        // dataIndex: 'paymentStatus',
        // render: (paymentStatus) => (
        //     <Space
        //         className={`centerMe ${
        //             paymentStatus === 'paid' ? 'isBlue' : ''
        //         }`}
        //     >
        //         <Checkbox checked={paymentStatus === 'paid'} />
        //     </Space>
        // ),
        // key: 'paymentStatus',

        title: <DynamicStrings id="OFFERS_STATUS" />,
        render: ({
            status,
            paymentStatus,
            trackingStatus,
            id,
            actualAmount,
            ...order
        }) => (
            <OrderStatus
                status={status}
                paymentStatus={paymentStatus}
                trackingStatus={trackingStatus}
                id={id}
                keyRequest={keyRequest}
                actualAmount={actualAmount}
                order={order}
            />
        ),
        key: 'paymentStatus',
        sorter: true,
    },
    {
        title: <DynamicStrings id="DIRECT_SALES_DELIVERED" />,
        dataIndex: 'trackingStatus',
        render: (e) => {
            const color = {
                delivered: 'green',
                on_the_way: 'blue',
                process: 'orange',
                prepared: 'yellow',
                delayed: 'red',
            }

            return <Badge color={color[e]} text={<DynamicStrings id={e} />} />
        },
        key: '',
    },
    {
        width: 50,
        render: (e) => {
            const menu = (
                <Menu>
                    <Menu.Item key="0">
                        <PDFDownloadLink
                            document={
                                <MyPDFDocument
                                    noSupplier
                                    lang="en"
                                    order={e}
                                    type={'direct'}
                                />
                            }
                            fileName={`${e.orderID}.pdf`}
                        >
                            {({ blob, url, loading, error }) =>
                                loading ? (
                                    <DynamicStrings
                                        id={'DIRECT_SALES_LOADING_PDF'}
                                    />
                                ) : (
                                    <DynamicStrings
                                        id={'DIRECT_SALES_DOWNLOAD_PDF_EN'}
                                    />
                                )
                            }
                        </PDFDownloadLink>
                    </Menu.Item>

                    <Menu.Item
                        key="1"
                        disabled={
                            e.paymentStatus === 'rfd' ||
                            !!e.refundVouchers?.length ||
                            !e.customer
                        }
                    >
                        <a
                            onClick={() =>
                                goTo({
                                    pathname: '/vouchers/create',
                                    customer: e.customer,
                                    refundOrder: e,
                                })
                            }
                        >
                            <DynamicStrings
                                id={
                                    !!e.refundVouchers?.length
                                        ? 'SERVICES_ORDER_GOT_VOUCHER'
                                        : 'SERVICES_ORDER_LIST_CREATE_VOUCHER'
                                }
                            />
                        </a>
                    </Menu.Item>

                    {e.type === 'daily' && (
                        <Menu.Item key="1">
                            <a onClick={() => goTo('/daily-orders/' + e.id)}>
                                <DynamicStrings id="ROLES_EDIT" />
                            </a>
                        </Menu.Item>
                    )}
                    {e.type !== 'daily' && e.channel === 'wechat_group' && (
                        <Menu.Item key="1">
                            <a
                                onClick={() =>
                                    goTo(`/offers/${e.offer?.id}/orders`)
                                }
                            >
                                <DynamicStrings id="ROLES_EDIT" />
                            </a>
                        </Menu.Item>
                    )}
                </Menu>
            )

            return (
                <Space className="centerMe">
                    <Dropdown overlay={menu} trigger={['click']}>
                        <a
                            className="ant-dropdown-link"
                            onClick={(e) => e.preventDefault()}
                        >
                            <MoreOutlined />
                        </a>
                    </Dropdown>
                </Space>
            )
        },
    },
    {
        width: 36,
        title: 'QR',
        key: '18',
        render: (order) => {
            if (order.type == 'daily') {
                return (
                    <PopUpQR
                        url={`${process.env.REACT_APP_PAYMENT_URL}/${order.id}`}
                        iconQRCode={order.community?.iconQRCode}
                    />
                )
            } else {
                return null
            }
        },
    },
]

const buildQrOpts = (size) => ({
    errorCorrectionLevel: 'H',
    type: 'image/jpeg',
    quality: 1,
    width: size,
    qzone: 4,
    margin: 0,
    color: {
        dark: '#000',
        light: '#fff',
    },
})

const toQrDataUrl = (url, size) =>
    new Promise((resolve, reject) => {
        QRCode.toDataURL(url, buildQrOpts(size), (err, uri) => {
            if (err) {
                reject(err)
                return
            }
            resolve(uri)
        })
    })

const mergeQrOnBackground = async (url, backgroundSrc, mergeOpts) => {
    const { width, height } = await loadImageSize(backgroundSrc)
    const overlay = scaleQrOverlay(width, height)
    const qrUri = await toQrDataUrl(url, overlay.size)

    return mergeImages(
        [
            { src: backgroundSrc },
            { src: qrUri, x: overlay.x, y: overlay.y },
        ],
        mergeOpts
    )
}

const PopUpQR = ({ url, iconQRCode }) => {
    const [img, setImg] = useState()

    useEffect(() => {
        let cancelled = false

        const run = async () => {
            const srcIMG = iconQRCode?.uri
                ? `${SERVER.baseURL}/storage/communities/${iconQRCode.uri}`
                : petqrbg

            try {
                const b64 = await mergeQrOnBackground(url, srcIMG, {
                    format: 'image/jpeg',
                    crossOrigin: 'Anonymous',
                })
                if (!cancelled) {
                    setImg(b64)
                }
            } catch {
                try {
                    const b64 = await mergeQrOnBackground(url, petqrbg, {
                        format: 'image/jpeg',
                    })
                    if (!cancelled) {
                        setImg(b64)
                    }
                } catch {
                    // ignore merge failures
                }
            }
        }

        run()

        return () => {
            cancelled = true
        }
    }, [url, iconQRCode])

    // return fee
    return img ? <Avatar shape="square" src={img} /> : null
}

export default DirectSalesOrder
